<script setup lang="ts">
import { ref } from 'vue'
import type { LineRichMenuInput } from '@/types/schema.generated'
import {
  useLineRichMenuNewPageQuery,
  useCreateLineRichMenuMutation,
} from './LineRichMenuNewPage.generated'
import LineRichMenuForm from '@/components/organisms/LineRichMenu/LineRichMenuForm.vue'
import LineMessageSendingIndexPageSideMenu from '@/pages/ChatMessageSendingJobIndexPage/ChatMessageSendingJobIndexPageSideMenu.vue'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import { useAlert } from '@/libs/alertPortal'
import { useRoute, useRouter } from 'vue-router'

const alert = useAlert()
const router = useRouter()
const route = useRoute('LineRichMenuNewPage')
const isSubmitting = ref(false)

const { result: queryResult } = useLineRichMenuNewPageQuery()

const createMutation = useCreateLineRichMenuMutation()

const handleSubmit = async (input: LineRichMenuInput) => {
  const res = await createMutation.mutate({
    input: {
      lineRichMenu: input,
    },
  })
  if (!res) {
    isSubmitting.value = false
    alert.error('リッチメニューの作成に失敗しました')
    return
  }

  if (res.errors) {
    isSubmitting.value = false
    console.error(res.errors)
    alert.error('リッチメニューの作成に失敗しました')
    return
  }

  const data = res.data
  if (!data) {
    isSubmitting.value = false
    alert.error('リッチメニューの作成に失敗しました')
    return
  }

  if (
    data.createLineRichMenu.__typename !== 'CreateLineRichMenuSuccessPayload'
  ) {
    if (
      data.createLineRichMenu.__typename === 'LineRichMenuErrorPayload' &&
      data.createLineRichMenu.reason === 'DUPLICATE_SEGMENT_PUBLISHED'
    ) {
      isSubmitting.value = false
      alert.error(
        '同じセグメントのリッチメニューは1つしか公開できません。公開するにはすでに公開されている同じセグメントのリッチメニューを非公開にしてください。'
      )
      return
    }

    isSubmitting.value = false
    alert.error('リッチメニューの作成に失敗しました')
    return
  }

  alert.success('リッチメニューを作成しました')

  // eslint-disable-next-line fp/no-mutating-methods
  router.push({
    name: 'LineRichMenuEditPage',
    params: {
      entranceGUID: route.params.entranceGUID,
      lineRichMenuGUID: data.createLineRichMenu.lineRichMenu.guid,
    },
  })
}
</script>

<template>
  <CenteredPageContainer :class="$style.page" wide>
    <div :class="[$style.page__main, $style.setting]">
      <LineMessageSendingIndexPageSideMenu />
      <section :class="$style.wrapper" v-if="queryResult">
        <div :class="$style.container">
          <LineRichMenuForm
            v-model:isSubmitting="isSubmitting"
            :userRegistrationInflowSourceDefinitions="
              queryResult.segmentConditionParameters.inflowSourceCondition
                .inflowSources
            "
            :clickURLs="
              queryResult.segmentConditionParameters.clickURLCondition.clickUrls
            "
            :plannerEnquetes="
              queryResult.segmentConditionParameters.enqueteAnsweredCondition
                .enquetes
            "
            @submit="handleSubmit"
          />
        </div>
      </section>
    </div>
  </CenteredPageContainer>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  width: 100%;
}

.container {
  background-color: $color-surface-primary;
  padding: 16px;
  border-radius: 10px;
}

.page {
  padding-bottom: 80px;

  &__header {
    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }

  &__main {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.setting {
  display: grid;
  align-items: flex-start;
  gap: 40px;
  grid-template-columns: 302px 1fr;
  grid-template-rows: auto;
}
</style>
